import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Filter, Grid, List, ChevronDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import apiService from '../services/nextlevelgames-api';
import { useStore } from '../context/StoreContext';

const sortToApiValue = {
  name: 'name',
  'price-low': 'price_asc',
  'price-high': 'price_desc',
  newest: 'newest',
};

const sortOptions = [
  { value: 'name', label: 'Name: A to Z' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'newest', label: 'Newest First' },
];

const perPageOptions = [25, 50, 100];

// Categories excluded from this page (dedicated pages, internal/admin, or non-product categories)
const EXCLUDED_ROOT_NAMES = new Set([
  'Trading Card Games',
  'Backroom',
  'Bulk',
  'Commander: Secrets of Strixhaven',
  'Great Deals',
  'Reporting',
  '[R] Referral Program',
  'Referral Program',
  '[R] TCG Accessories',
  'Sale',
  'Singles',
  'Snacks & Drinks',
  'Tournaments',
  'XYAB',
]);

// Categories nested under another parent that should instead be shown as their own
// top-level entry on this page (the sidebar only supports two levels)
const PROMOTED_CATEGORY_IDS = new Set([
  '4P3LPOULKVVMYE5BERKQQQ5B', // All The Things Custom Designs
]);

const OtherShop = () => {
  const [searchParams] = useSearchParams();
  const { locations, selectedLocation, setSelectedLocation } = useStore();

  const [otherCategories, setOtherCategories] = useState([]);
  const [allOtherIds, setAllOtherIds] = useState([]);

  const [expandedParentId, setExpandedParentId] = useState(null);
  const [activeCategoryIds, setActiveCategoryIds] = useState([]);

  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 1024);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [storeOnly, setStoreOnly] = useState(false);

  // Load category tree, exclude TCG and other dedicated-page roots
  useEffect(() => {
    apiService.getCategoryTree().then(data => {
      // Pull promoted categories out of whichever parent they're nested under
      // so they show as their own top-level entry
      const promoted = [];
      const strippedRoots = (data.categories || []).map(parent => ({
        ...parent,
        children: (parent.children || []).filter(child => {
          if (PROMOTED_CATEGORY_IDS.has(child.id)) {
            promoted.push(child);
            return false;
          }
          return true;
        }),
      }));

      const roots = [...strippedRoots, ...promoted].filter(c => !EXCLUDED_ROOT_NAMES.has(c.name) && !c.name.startsWith('[R]'));
      setOtherCategories(roots);

      const filtered = roots.map(parent => ({
        ...parent,
        children: (parent.children || []).filter(c => !c.name.startsWith('[R]')),
      }));

      const ids = [];
      filtered.forEach(parent => {
        ids.push(parent.id);
        parent.children.forEach(child => ids.push(child.id));
      });
      setOtherCategories(filtered);
      setAllOtherIds(ids);
    }).catch(() => {});
  }, []);

  // Handle ?category_id= preselect once tree is loaded
  useEffect(() => {
    if (!otherCategories.length || !allOtherIds.length) return;
    const preselect = searchParams.get('category_id');
    if (preselect) {
      const parent = otherCategories.find(p => p.id === preselect);
      if (parent) {
        setExpandedParentId(parent.id);
        setActiveCategoryIds([parent.id, ...(parent.children || []).map(c => c.id)]);
        return;
      }
      for (const parent of otherCategories) {
        if ((parent.children || []).find(c => c.id === preselect)) {
          setExpandedParentId(parent.id);
          setActiveCategoryIds([preselect]);
          return;
        }
      }
    }
    setActiveCategoryIds(allOtherIds);
  }, [otherCategories, allOtherIds, searchParams]);

  const fetchItems = useCallback(async (categoryIds, sort, locationIds, limit, offset, onlineOnlyFlag, storeOnlyFlag) => {
    if (!categoryIds.length) return;
    setLoading(true);
    setError(null);
    try {
      const data = await apiService.getCatalogImages(
        limit, offset, [], sortToApiValue[sort] || 'name', locationIds, [], [], categoryIds, onlineOnlyFlag, storeOnlyFlag
      );
      setCatalogItems((data.items || []).map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        description_html: item.description_html,
        price: item.price_money ? item.price_money.amount / 100 : 0,
        imageUrl: item.image_urls?.[0] || null,
        variation_id: item.variation_id,
        variation_name: item.variation_name,
        location_id: item.location_id,
        present_at_location_ids: item.present_at_location_ids || [],
        quantities_by_location: item.quantities_by_location || {},
        online_available: item.online_available === true,
      })));
      setHasMore(Boolean(data.has_more));
      setTotalResults(data.total ?? 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const selectedLocationIds = selectedLocation ? [selectedLocation.id] : [];

  useEffect(() => {
    if (!activeCategoryIds.length) return;
    setCurrentPage(0);
    fetchItems(activeCategoryIds, sortBy, selectedLocationIds, itemsPerPage, 0, onlineOnly, storeOnly);
  }, [activeCategoryIds, sortBy, selectedLocation, itemsPerPage, onlineOnly, storeOnly, fetchItems]);

  const handleSelectParent = (parent) => {
    if (expandedParentId === parent.id) {
      setExpandedParentId(null);
      setActiveCategoryIds(allOtherIds);
    } else {
      setExpandedParentId(parent.id);
      setActiveCategoryIds([parent.id, ...(parent.children || []).map(c => c.id)]);
    }
  };

  const handleSelectChild = (e, childId, parentId) => {
    e.stopPropagation();
    setExpandedParentId(parentId);
    setActiveCategoryIds([childId]);
  };

  const handleSelectAll = () => {
    setExpandedParentId(null);
    setActiveCategoryIds(allOtherIds);
  };

  const handleNextPage = () => {
    if (!hasMore) return;
    const next = currentPage + 1;
    setCurrentPage(next);
    fetchItems(activeCategoryIds, sortBy, selectedLocationIds, itemsPerPage, next * itemsPerPage, onlineOnly, storeOnly);
    window.scrollTo(0, 0);
  };

  const handlePreviousPage = () => {
    if (currentPage === 0) return;
    const prev = currentPage - 1;
    setCurrentPage(prev);
    fetchItems(activeCategoryIds, sortBy, selectedLocationIds, itemsPerPage, prev * itemsPerPage, onlineOnly, storeOnly);
    window.scrollTo(0, 0);
  };

  const isAllSelected = expandedParentId === null && activeCategoryIds.length === allOtherIds.length;
  const activeChildId = activeCategoryIds.length === 1 ? activeCategoryIds[0] : null;

  // Derive a page title from the active selection
  const activeParent = expandedParentId ? otherCategories.find(p => p.id === expandedParentId) : null;
  const pageTitle = activeParent ? activeParent.name : 'Other Products';

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">{pageTitle}</h1>
          {totalResults > 0 && (
            <p className="text-gray-600">
              {currentPage * itemsPerPage + 1}–{Math.min((currentPage + 1) * itemsPerPage, totalResults)} of {totalResults} results
              {selectedLocation && <span className="ml-1">at {selectedLocation.name}</span>}
            </p>
          )}
        </div>

        {/* Controls Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${showFilters ? 'bg-primary-100 text-primary-600 border-primary-300' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>
              <div className="flex items-center gap-2 border-l pl-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:border-primary-500"
                >
                  {sortOptions.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" />
              </div>

              <div className="hidden md:flex items-center gap-2">
                <span className="text-gray-700">Show:</span>
                <div className="relative">
                  <select
                    value={itemsPerPage}
                    onChange={e => setItemsPerPage(Number(e.target.value))}
                    className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:border-primary-500"
                  >
                    {perPageOptions.map(o => (
                      <option key={o} value={o}>{o} per page</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <AnimatePresence>
            {showFilters && (
              <motion.aside
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="w-64 flex-shrink-0"
              >
                <div className="bg-white rounded-lg shadow-sm p-6">
                  {locations.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Store Location</h3>
                      <div className="space-y-2">
                        {locations.map(loc => (
                          <label key={loc.id} className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="store-location-other"
                              checked={selectedLocation?.id === loc.id}
                              onChange={() => {}}
                              onClick={() => setSelectedLocation(selectedLocation?.id === loc.id ? null : loc)}
                              className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 flex-shrink-0"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-800">{loc.name}</div>
                              <div className="text-xs text-gray-500">{loc.address?.address_line1}, {loc.address?.locality}, {loc.address?.administrative_district_level1}</div>
                            </div>
                          </label>
                        ))}
                        {selectedLocation && (
                          <button
                            onClick={() => setSelectedLocation(null)}
                            className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                          >
                            Show all locations
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="border-t pt-5 mb-6">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Availability</h3>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={onlineOnly}
                          onChange={e => { setOnlineOnly(e.target.checked); if (e.target.checked) setStoreOnly(false); }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Available Online</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={storeOnly}
                          onChange={e => { setStoreOnly(e.target.checked); if (e.target.checked) setOnlineOnly(false); }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">In Store Only</span>
                      </label>
                    </div>
                  </div>

                  <div className="border-t pt-5 mb-6">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Browse by category</h3>
                    <button
                      onClick={handleSelectAll}
                      className={`block w-full text-left px-2 py-1.5 rounded text-sm mb-1 transition-colors ${isAllSelected ? 'text-primary-600 font-semibold' : 'text-gray-700 hover:text-primary-600'}`}
                    >
                      All Items
                    </button>
                    {otherCategories.map(parent => {
                      const isExpanded = expandedParentId === parent.id;
                      const isParentActive = isExpanded && (activeCategoryIds.length > 1 || activeCategoryIds[0] === parent.id);
                      return (
                        <div key={parent.id}>
                          <button
                            onClick={() => handleSelectParent(parent)}
                            className={`flex items-center justify-between w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${isParentActive ? 'text-primary-600' : 'text-gray-800 hover:text-primary-600'}`}
                          >
                            <span className={isParentActive ? 'font-bold' : 'font-semibold'}>{parent.name}</span>
                            {(parent.children || []).length > 0 && (
                              <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            )}
                          </button>
                          <AnimatePresence>
                            {isExpanded && (parent.children || []).length > 0 && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                {parent.children.map(child => (
                                  <button
                                    key={child.id}
                                    onClick={e => handleSelectChild(e, child.id, parent.id)}
                                    className={`block w-full text-left pl-6 pr-2 py-1 rounded text-sm transition-colors ${activeChildId === child.id ? 'text-primary-600 font-medium' : 'text-gray-600 hover:text-primary-600'}`}
                                  >
                                    {child.name}
                                  </button>
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => { handleSelectAll(); setSelectedLocation(null); setOnlineOnly(false); setStoreOnly(false); }}
                    className="w-full py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                  >
                    Clear All Filters
                  </button>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>

          {/* Products */}
          <div className="flex-1 min-h-screen">
            {loading ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" />
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <p className="text-red-500 text-lg">{error}</p>
              </div>
            ) : catalogItems.length > 0 ? (
              <>
                <div className={viewMode === 'grid'
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                  : 'space-y-4'
                }>
                  {catalogItems.map((item, index) => (
                    <motion.div
                      key={item.variation_id || item.id || item.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.03 }}
                    >
                      <ProductCard
                        product={{
                          name: item.name,
                          price: item.price,
                          description: item.description,
                          description_html: item.description_html,
                          variation_id: item.variation_id,
                          variation_name: item.variation_name,
                          location_id: item.location_id,
                          present_at_location_ids: item.present_at_location_ids,
                          quantities_by_location: item.quantities_by_location,
                          selectedLocationIds,
                          online_available: item.online_available,
                        }}
                        imageUrl={item.imageUrl}
                        viewMode={viewMode}
                      />
                    </motion.div>
                  ))}
                </div>

                {(currentPage > 0 || hasMore) && (
                  <div className="flex items-center justify-center gap-3 mt-8">
                    {currentPage > 0 && (
                      <button
                        onClick={handlePreviousPage}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Previous Page
                      </button>
                    )}
                    {hasMore && (
                      <button
                        onClick={handleNextPage}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        Next Page
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No products found.</p>
                {selectedLocation && (
                  <p className="text-sm text-gray-400 mt-2">
                    No items available at {selectedLocation.name} for this category.{' '}
                    <button onClick={() => setSelectedLocation(null)} className="text-primary-600 hover:underline">
                      Show all locations
                    </button>
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtherShop;
